import { parseApiError } from './apiError';

// Historique des discussions « Contacter le support » de l'app. Lecture
// seule : l'équipe répond depuis Telegram, le back-office consulte.
const base = () => `${process.env.REACT_APP_API_URL}api/support/admin/conversations`;

const authHeaders = (token: string) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

export interface SupportConversationRow {
  id_conversation: string;
  user_id: string;
  status: 'open' | 'closed';
  closed_by: 'user' | 'admin' | 'timeout' | null;
  agent_name: string | null;
  rental_id: string | null;
  rating: 'up' | 'down' | null;
  lang: string | null;
  created_at: string;
  last_message_at: string;
  closed_at: string | null;
  user_first_name: string;
  user_last_name: string;
  user_email: string | null;
  user_phone: string | null;
  message_count: number;
  admin_message_count: number;
  last_message_body: string | null;
  last_message_sender: 'user' | 'admin' | null;
}

export interface SupportQuote {
  id_message: string;
  sender: 'user' | 'admin' | null;
  author_name: string | null;
  body: string;
  has_image: boolean;
}

export interface SupportMessage {
  id_message: string;
  sender: 'user' | 'admin' | 'system';
  body: string;
  author_name: string | null;
  event: 'assigned' | 'closed' | 'welcome' | 'welcome_closed' | 'reminder' | null;
  meta: Record<string, string> | null;
  /** Photo jointe, derrière une route authentifiée : à charger avec le jeton. */
  image_url: string | null;
  reply_to: SupportQuote | null;
  /** Lu par l'autre partie ; null tant que non. */
  read_at: string | null;
  created_at: string;
}

export interface SupportConversationDetail {
  conversation: Omit<SupportConversationRow, 'message_count' | 'admin_message_count' | 'last_message_body' | 'last_message_sender'>;
  messages: SupportMessage[];
}

export const getSupportConversations = async (
  token: string,
  status: 'open' | 'closed' | 'all' = 'all'
): Promise<SupportConversationRow[]> => {
  const query = status === 'all' ? '' : `?status=${status}`;
  const res = await fetch(`${base()}${query}`, { headers: authHeaders(token) });
  if (!res.ok) throw await parseApiError(res, 'Erreur lors du chargement des discussions');
  return res.json();
};

export const getSupportConversation = async (
  id: string,
  token: string
): Promise<SupportConversationDetail> => {
  const res = await fetch(`${base()}/${id}`, { headers: authHeaders(token) });
  if (!res.ok) throw await parseApiError(res, 'Erreur lors du chargement de la discussion');
  return res.json();
};

/**
 * Une photo du fil, en URL d'objet. Le fichier n'est pas servi en statique :
 * un <img src> nu recevrait un 401, il faut passer le jeton.
 */
export const fetchSupportPhoto = async (url: string, token: string): Promise<string> => {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw await parseApiError(res, 'Photo indisponible');
  return URL.createObjectURL(await res.blob());
};

export interface SupportAgentStats {
  agent_name: string | null;
  conversations: number;
  answered: number;
  avg_first_response_seconds: number | null;
  rated_up: number;
  rated_down: number;
  last_7_days: number;
}

export interface SupportStats {
  total: SupportAgentStats | null;
  agents: SupportAgentStats[];
  unassigned: SupportAgentStats | null;
}

export const getSupportStats = async (token: string): Promise<SupportStats> => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}api/support/admin/stats`, { headers: authHeaders(token) });
  if (!res.ok) throw await parseApiError(res, 'Erreur lors du chargement des statistiques');
  return res.json();
};
